import React, { createContext, useContext, useEffect, useMemo } from 'react';

import type { Breakpoint } from '../../utilities/useViewport';
import type {
  ShellContentProps,
  ShellFooterProps,
  ShellHeaderProps,
  ShellProps,
} from '../Shell/Shell';
import { sizing } from '../../common/sizing';
import { zIndexes } from '../../common/z_indexes';
import { colors, darkThemeSelector, styled } from '../../stitches.config';
import { useViewport } from '../../utilities/useViewport';
import { Shell, ShellContent, ShellFooter, ShellHeader } from '../Shell/Shell';

export type DrawerPreset = 'initial' | 'wide' | 'extra-wide';

/**
 * Descriptor for drawer width used by drawer-aware Pane styling. Mirrors Drawer's
 * preset and width/minWidth/maxWidth so padding can match the actual drawer size.
 */
export type DrawerWidthDescriptor = {
  preset?: DrawerPreset;
  width?: number | string;
  minWidth?: number | string;
  maxWidth?: number | string;
};

export type DrawerWidthContextValue = {
  descriptor: DrawerWidthDescriptor | null;
  setDescriptor: (descriptor: DrawerWidthDescriptor | null) => void;
};

const noop = () => {};

export const DrawerWidthContext = createContext<DrawerWidthContextValue>({
  descriptor: null,
  setDescriptor: noop,
});

export function useDrawerWidthContext(): DrawerWidthContextValue {
  return useContext(DrawerWidthContext);
}

const DrawerHeaderContainer = styled(ShellHeader);
DrawerHeaderContainer.displayName = 'DrawerHeaderContainer';

export function DrawerHeader(props: ShellHeaderProps) {
  return <DrawerHeaderContainer {...props} />;
}

export type DrawerContentProps = ShellContentProps & { ref?: React.Ref<HTMLDivElement | null> };

export const DrawerContentContainer = styled(ShellContent);
DrawerContentContainer.displayName = 'DrawerContentContainer';

export function DrawerContent({ children, ref, ...remaining }: DrawerContentProps) {
  return (
    <DrawerContentContainer data-drawer-content ref={ref} {...remaining}>
      {children}
    </DrawerContentContainer>
  );
}

const DrawerFooterContainer = styled(ShellFooter);
DrawerFooterContainer.displayName = 'DrawerFooterContainer';

export type DrawerFooterProps = ShellFooterProps & {
  /**
   * Provide primary actions for your drawer.
   */
  actions?: React.ReactNode;
  /**
   * Provide secondary actions, positioned at the start of the footer.
   */
  secondaryActions?: React.ReactNode;
  ref?: React.Ref<HTMLDivElement | null>;
};

export function DrawerFooter({ actions, secondaryActions, ref, ...remaining }: DrawerFooterProps) {
  return <DrawerFooterContainer ref={ref} start={secondaryActions} end={actions} {...remaining} />;
}

// Guarantees a minimum internal width for the Drawer.
export const drawerWidth = sizing.primary * 2 + 320;

const drawerWidthRem = `${drawerWidth / 16}rem`;

function toPaddingValue(value: number | string): string {
  if (typeof value === 'number') {
    return `${value}px`;
  }
  return value;
}

const PRESET_PADDING: Record<DrawerPreset, string> = {
  initial: drawerWidthRem,
  wide: '40vw',
  'extra-wide': '80vw',
};

/**
 * Returns the CSS value to use for pane padding when drawer-aware, matching the
 * Drawer's effective width (preset or custom width/minWidth/maxWidth). Use this
 * when setting --drawer-padding on Pane so header/content/footer padding matches
 * the drawer. On non-desktop breakpoints returns '0' (drawer is full-screen).
 */
export function getDrawerPaddingValue(
  descriptor: DrawerWidthDescriptor | true,
  breakpoint: Breakpoint,
): string {
  if (breakpoint !== 'desktop') {
    return '0';
  }
  if (descriptor === true) {
    return drawerWidthRem;
  }
  if (descriptor.width !== undefined && descriptor.width !== null) {
    return toPaddingValue(descriptor.width);
  }
  if (descriptor.preset !== undefined) {
    return PRESET_PADDING[descriptor.preset];
  }
  if (descriptor.minWidth !== undefined && descriptor.minWidth !== null) {
    return toPaddingValue(descriptor.minWidth);
  }
  if (descriptor.maxWidth !== undefined && descriptor.maxWidth !== null) {
    return toPaddingValue(descriptor.maxWidth);
  }
  return drawerWidthRem;
}

export const DrawerContainer = styled(Shell, {
  position: 'relative',
  zIndex: zIndexes.drawer,
  gridArea: 'drawer',
  overflow: 'hidden',
  backgroundColor: colors.bgApplicationLight,
  strokeAll: colors.strokeNeutralLight,
  pointerEvents: 'auto',

  [darkThemeSelector]: {
    backgroundColor: colors.bgApplicationDark,
    strokeAll: colors.strokeNeutralDark,
  },

  variants: {
    preset: {
      initial: {
        '@desktop': {
          width: `$${drawerWidth}`,
          minWidth: `$${drawerWidth}`,
        },
      },
      wide: {
        '@desktop': {
          width: `40vw`,
          minWidth: `40vw`,
        },
      },
      'extra-wide': {
        '@desktop': {
          width: `80vw`,
          minWidth: `80vw`,
        },
      },
    },
  },
});
DrawerContainer.displayName = 'DrawerContainer';

export type DrawerProps = ShellProps & {
  preset?: DrawerPreset;
};

function buildDescriptorFromProps(props: {
  preset?: DrawerPreset;
  width?: DrawerProps['width'];
  minWidth?: DrawerProps['minWidth'];
  maxWidth?: DrawerProps['maxWidth'];
}): DrawerWidthDescriptor {
  const { preset, width, minWidth, maxWidth } = props;
  const descriptor: DrawerWidthDescriptor = {};
  if (preset !== undefined) descriptor.preset = preset;
  if (width !== undefined && width !== null) descriptor.width = width;
  if (minWidth !== undefined && minWidth !== null) descriptor.minWidth = minWidth;
  if (maxWidth !== undefined && maxWidth !== null) descriptor.maxWidth = maxWidth;
  return descriptor;
}

export function Drawer({
  children,
  contentMode = 'fit',
  preset = 'initial',
  width,
  maxWidth,
  minWidth,
  ref,
  ...remaining
}: DrawerProps) {
  const { breakpoint } = useViewport();
  const { setDescriptor } = useDrawerWidthContext();
  const descriptor = useMemo(
    () => buildDescriptorFromProps({ preset, width, minWidth, maxWidth }),
    [preset, width, minWidth, maxWidth],
  );

  useEffect(() => {
    setDescriptor(descriptor);
    return () => setDescriptor(null);
  }, [descriptor, setDescriptor]);

  return (
    <DrawerContainer
      ref={ref}
      contentMode={contentMode}
      preset={preset}
      width={breakpoint !== 'desktop' ? '100%' : width}
      maxWidth={breakpoint !== 'desktop' ? '100%' : maxWidth}
      minWidth={breakpoint !== 'desktop' ? '100%' : minWidth}
      type="drawer"
      {...remaining}
    >
      {children}
    </DrawerContainer>
  );
}
